import {
  Popover,
  Space,
  Timeline
} from "antd";
import Details from "../../../components/detailsList";
import { useTaxLegalEvaluation } from "../../../contexts/taxLegalCaseStore";
import { useTaxLegalStages } from "../../../contexts/TaxLegalStagesStore";
import _t from "../../../lang/translate";
import { TaxLegalStageData } from "../../../types/taxLegalTypes";
import format from "../../../utilities/formatNumbers";
import { isValidNum } from "../../../utilities/typeGuard";
import { ComplaintConclusionForm } from "../stages/complaint";
import { ConclusionForm, HearingOfPartiesForm } from "../stages/priceCheck";

export default function PriceDifferenceTimeline() {
  const { stages } = useTaxLegalStages();
  const evaluation = useTaxLegalEvaluation();

  return (
    <div className="mt-3">
      <Timeline className="" mode="left">
        <Timeline.Item
          color="blue"
          label={
            <>
              {_t("reviewDate")} <small>{format.date(evaluation?.reviewDate)}</small>
            </>
          }
        >
          <Popover
            title={_t("original", "evaluation")}
            content={
              <Details borderLess>
                <Details.Item label={_t("new_price")} value={format.number(evaluation?.newPrice)} />
                <Details.Item
                  label={_t("equipment")}
                  value={format.number(evaluation?.equipmentsPrice)}
                />
                <Details.Item
                  label={_t("trade_price")}
                  value={format.number(evaluation?.tradePrice)}
                />
                <Details.Item
                  label={_t("registration_fee_short")}
                  value={format.number(evaluation?.registrationFee)}
                />
              </Details>
            }
          >
            {format.number(evaluation?.registrationFee)}
          </Popover>
        </Timeline.Item>
        {stages.map((stage, i) => {
          const color = i < stages.length - 1 ? "green" : "gray";
          return (
            <Timeline.Item
              color={color}
              key={stage.fingerprint}
              label={
                <>
                  {_t(stage.type)} <small>{format.date(stage.timestamp)}</small>
                </>
              }
            >
              <Space direction="vertical" size="small">
                {Object.keys(stage.data).map((key) => {
                  const data = stage.data[key as keyof TaxLegalStageData];
                  const shouldRender = (
                    data: TaxLegalStageData[keyof TaxLegalStageData],
                  ): data is HearingOfPartiesForm | ConclusionForm | ComplaintConclusionForm => {
                    if (data && "regulationAmount" in data) {
                      return (
                        isValidNum(data.newPrice) ||
                        isValidNum(data.equipmentsPrice) ||
                        isValidNum(data.tradePrice) ||
                        isValidNum(data.registrationFee) ||
                        isValidNum(data.regulationAmount)
                      );
                    }
                    return false;
                  };
                  if (shouldRender(data)) {
                    const regulation = data.regulationAmount;
                    const newPrice = data.newPrice;
                    const equipmentsPrice = data.equipmentsPrice;
                    const tradePrice = data.tradePrice;
                    const registrationFee = data.registrationFee;
                    return (
                      <Popover
                        key={key}
                        title={`${_t(key as keyof TaxLegalStageData)}: ${_t("ms_value")} (${_t("diff")})`}
                        content={
                          <Details borderLess>
                            <Details.Item
                              label={_t("new_price")}
                              value={`${format.number(newPrice)} (${format.number(newPrice - (evaluation?.newPrice || 0))})`}
                            />
                            <Details.Item
                              label={_t("equipment")}
                              value={`${format.number(equipmentsPrice)} (${format.number(equipmentsPrice - (evaluation?.equipmentsPrice || 0))})`}
                            />
                            <Details.Item
                              label={_t("trade_price")}
                              value={`${format.number(tradePrice)} (${format.number(tradePrice - (evaluation?.tradePrice || 0))})`}
                            />
                            <Details.Item
                              label={_t("registration_fee_short")}
                              value={`${format.number(registrationFee)} (${format.number(registrationFee - (evaluation?.registrationFee || 0))})`}
                            />
                          </Details>
                        }
                      >
                        <span>
                          {format.number(registrationFee)} ({format.number(regulation)})
                        </span>
                      </Popover>
                    );
                  }
                  return null;
                })}
              </Space>
            </Timeline.Item>
          );
        })}
      </Timeline>
      <small>{_t("msg.valuesGivenInKr")}</small>
    </div>
  );
}
